import * as React from 'react';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import ImageCropper from 'src/components/ImageCropper/ImageCropper/ImageCropper';
import styles from './ImageCropperModal.styles';
import { isNil } from 'lodash';
import { toast } from 'react-toastify';
import { classes } from 'typestyle';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';
import { TemplateModalTitle } from 'src/components/TemplateModalWithButton/TemplateModalWithButton';
import { uploadImage } from 'src/utils/Images/ImageUpload.client';

type Props = {
  open: boolean;
  passedImage: string;
  /** The 'pending' image data URL that was set by drag & drop - until saved or canceled by user */
  initialImageDataUrl?: string | null;
  returnImage: (image: string) => void;
  closeModal: () => void;
};
enum ActivityStatus {
  STATIC,
  UPLOADING,
  UPLOADED,
}
type State = {
  uploadedImage: string | null;
  isDragging: boolean;
  activityStatus: ActivityStatus;
};

export default class ImageCropperModal extends React.Component<Props, State> {
  imageCropper: React.RefObject<ImageCropper> = React.createRef();

  constructor(props: Props) {
    super(props);
    this.state = {
      uploadedImage: null,
      isDragging: false,
      activityStatus: ActivityStatus.STATIC,
    };
  }

  // needed for pending image after drag&drop to the grid
  componentDidMount() {
    if (this.props.open && this.props.initialImageDataUrl) {
      this.setState({ uploadedImage: this.props.initialImageDataUrl });
    }
  }

  isAsyncProcessing = () => {
    return this.state.activityStatus === ActivityStatus.UPLOADING;
  };

  handleCancel = () => {
    if (this.isAsyncProcessing()) {
      return;
    }
    const { closeModal } = this.props;
    this.setState({ uploadedImage: null }, closeModal);
  };

  handleSave = async () => {
    if (this.isAsyncProcessing()) {
      return;
    }
    try {
      if (this.imageCropper.current) {
        this.setState({
          activityStatus: ActivityStatus.UPLOADING,
        });
        const newImageBlob = await this.imageCropper.current.getCroppedImage();
        if (!isNil(newImageBlob)) {
          const fileName = await uploadImage({ blob: newImageBlob, mime: newImageBlob.type });
          this.setState({ uploadedImage: fileName, activityStatus: ActivityStatus.UPLOADED });
          this.props.returnImage(fileName);
        }
      }
    } catch (_error) {
      this.setState({ activityStatus: ActivityStatus.STATIC });
      toast.error('An Error Occurred Trying To Save Image');
    }
  };

  handleImageUpload = (file: File) => {
    if (!file.type.startsWith('image/')) {
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      this.setState({ uploadedImage: reader.result as string });
    };
    reader.readAsDataURL(file);
  };

  handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      this.handleImageUpload(e.target.files[0]);
    }
  };

  handlePaste = (e: React.ClipboardEvent) => {
    // The dedicated paste target is contentEditable so browsers expose their native
    // Paste command. Never allow clipboard text or HTML to modify the React DOM.
    e.preventDefault();
    e.stopPropagation();

    if (this.isAsyncProcessing()) {
      return;
    }

    const clipboardItems = e.clipboardData.items;
    for (let index = 0; index < clipboardItems.length; index++) {
      const clipboardItem = clipboardItems[index];
      if (clipboardItem.kind !== 'file' || !clipboardItem.type.startsWith('image/')) {
        continue;
      }

      const imageFile = clipboardItem.getAsFile();
      if (imageFile) {
        this.handleImageUpload(imageFile);
        return;
      }
    }
  };

  handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: true });
  };
  handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
  };
  handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      this.handleImageUpload(files[0]);
    }
  };

  getValue = () => {
    return this.getImage();
  };

  getImage = () => {
    return this.state.uploadedImage ?? this.props.passedImage;
  };

  isPopup() {
    return true;
  }

  render() {
    const { open } = this.props;
    const { isDragging } = this.state;
    const image = this.getImage();
    const inProgress = this.isAsyncProcessing();

    return (
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown') {
            this.handleCancel();
          }
        }}
        onDragEnter={this.handleDragEnter}
        onDragLeave={this.handleDragLeave}
        onDragOver={this.handleDragOver}
        onDrop={this.handleDrop}
        onPaste={this.handlePaste}
        className="ag-custom-component-popup"
      >
        <div className={styles.titleContainer}>
          <TemplateModalTitle title={'Crop Image'} onCancel={this.handleCancel} />
        </div>
        <div className={styles.inputFileContainer}>
          <input type="file" accept="image/*" onChange={this.handleFileInputChange} />
          <Box
            contentEditable={!inProgress}
            suppressContentEditableWarning
            className={styles.pasteTarget}
            onBeforeInput={(event) => event.preventDefault()}
          />
        </div>

        <div className={styles.dialogBody}>
          {isDragging ? (
            <div
              style={{ cursor: isDragging && 'grab' }}
              className={classes(styles.dropContainer, isDragging && styles.dragActive)}
            >
              Release to upload
            </div>
          ) : (
            <div className={styles.cropperContainer}>
              <ImageCropper key={image} ref={this.imageCropper} image={image} />
            </div>
          )}
        </div>
        <div className={styles.buttonContainer}>
          <SquareActionButton
            type="secondary"
            onClick={this.handleCancel}
            text="Cancel"
            className={styles.buttonSave}
            disabled={inProgress}
          />
          <SquareActionButton
            onClick={this.handleSave}
            text="Save"
            className={styles.buttonSave}
            disabled={inProgress}
            loading={inProgress}
          />
        </div>
      </Dialog>
    );
  }
}
